'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@src/v3/components/coreUI/dialog';
import Button from '@src/v3/components/coreUI/button';
import Icon from '@src/v3/components/coreUI/icon';
import { PendingInvite } from '@src/v3/interfaces/organization';
import { APP_ROUTE } from '@src/v3/constants/routes';

interface OrganizationInviteModalProps {
  invites: PendingInvite[];
  // eslint-disable-next-line no-unused-vars
  onAccept: (token: string) => Promise<{ success: boolean; error?: string }>;
  // eslint-disable-next-line no-unused-vars
  onReject: (token: string) => Promise<{ success: boolean; error?: string }>;
}

const OrganizationInviteModal: React.FC<OrganizationInviteModalProps> = ({
  invites,
  onAccept,
  onReject,
}) => {
  const router = useRouter();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Don't render if no invites
  if (invites.length === 0) return null;

  // Always show the first invite in the list; the parent removes declined invites
  // from the array so the next one naturally becomes index 0.
  const currentInvite = invites[0];

  const handleAccept = async () => {
    setIsProcessing(true);
    setError(null);

    const result = await onAccept(currentInvite.invite_token);

    if (!result.success) {
      setError(result.error || 'Failed to accept invitation');
      setIsProcessing(false);
      return;
    }

    // Accepting one invite will reject all others via the backend
    // Redirect to organization settings page after successful acceptance
    setIsProcessing(false);
    router.push(APP_ROUTE.ORGANIZATION_SETTINGS);
  };

  const handleReject = async () => {
    setIsProcessing(true);
    setError(null);

    const result = await onReject(currentInvite.invite_token);

    if (!result.success) {
      setError(result.error || 'Failed to reject invitation');
      setIsProcessing(false);
      return;
    }

    // The parent removes the declined invite from the array,
    // so the next invite naturally becomes invites[0].
    // If no more invites remain, shouldShowInviteModal becomes false and the modal unmounts.
    setIsProcessing(false);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'SUPERADMIN':
        return 'Super Admin';
      case 'ADMIN':
        return 'Admin';
      case 'MEMBER':
        return 'Member';
      default:
        return role;
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-md max-h-[85vh] overflow-y-auto p-4 sm:p-6"
        // Prevent closing via escape or backdrop click
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        // Hide the close button
        showCloseButton={false}
      >
        <div className="flex flex-col items-center text-center mb-4">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Icon
              iconName="buildings"
              iconProps={{ className: 'w-8 h-8 text-gray-600' }}
            />
          </div>
          <DialogTitle className="text-xl">
            You&apos;ve Been Invited
          </DialogTitle>
          <DialogDescription className="mt-1">
            You have been invited to join an organization on Zeme.
          </DialogDescription>
        </div>

        <div className="mt-4 sm:mt-6 space-y-4">
          {/* Organization Card */}
          <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                <span className="text-xl font-bold text-gray-700">
                  {currentInvite.organization_name.charAt(0)}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 break-words">
                  {currentInvite.organization_name}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Invited as {getRoleLabel(currentInvite.role)}
                </p>
              </div>
            </div>

            {currentInvite.invited_at && (
              <p className="text-xs text-gray-400 mt-3">
                Invited{' '}
                {new Date(currentInvite.invited_at).toLocaleDateString(
                  'en-US',
                  {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }
                )}
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Info Text */}
          <p className="text-xs text-gray-500 text-center">
            {invites.length > 1 && (
              <span className="block mb-1">
                You have {invites.length} pending invitations. Accepting one
                will decline the others.
              </span>
            )}
            You can only be a member of one organization at a time.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleReject}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Decline'}
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleAccept}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Accept & Join'}
            </Button>
          </div>

          {/* Invite Counter */}
          {invites.length > 1 && (
            <p className="text-xs text-gray-400 text-center">
              Invitation 1 of {invites.length}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationInviteModal;
